/**
 * Native store purchases: Google Play Billing (Android) and StoreKit 2 (iOS) through
 * @capgo/native-purchases.
 *
 * A purchase is finished only after the API has verified it with the store and credited the gems:
 *   store sheet -> receipt saved to a local outbox -> POST /v1/iap/verify -> finish -> outbox cleared.
 * The API consumes Android consumables (which also acknowledges them); the app acknowledges too, so a
 * failed server-side consume can't trigger Google's 3-day auto-refund. Interrupted purchases (app
 * killed, offline, pending payment, Ask to Buy) are retried on the next launch or resume, from the
 * outbox and from the store's own list of unfinished purchases for this account.
 */
import { GEM_PACKS } from '@vm/content';
import type { GemPack, VerifyPurchaseResponse } from '@vm/shared';
import type { Transaction } from '@capgo/native-purchases';
import { errorCode, errorMessage, isNetworkError } from './api/http';
import { api } from './api/routes';
import { platform } from './platform';

type Store = 'android' | 'ios';

/** A store receipt waiting to be credited. */
export interface StoreReceipt {
  platform: Store;
  packId: string;
  /** Play purchase token, or the StoreKit signed transaction (JWS). */
  receipt: string;
  transactionId?: string;
  /** Account that bought it; only that account ever submits it. */
  userId: string;
  at: number;
}

export type StartResult =
  | { kind: 'purchased'; receipt: StoreReceipt }
  | { kind: 'cancelled' }
  /** Pending payment or Ask to Buy: the gems arrive when the store completes it. */
  | { kind: 'deferred' }
  | { kind: 'failed'; message: string };

export type SettleResult =
  | { kind: 'credited'; res: VerifyPurchaseResponse }
  | { kind: 'pending'; res: VerifyPurchaseResponse }
  /** Temporary problem (offline, server busy); the receipt stays in the outbox. */
  | { kind: 'retry'; message: string }
  | { kind: 'rejected'; message: string };

const OUTBOX_KEY = 'vm.iap.outbox';
const NOT_SOLD_HERE = 'Gem packs for this platform are sold through the Voxel Marines app on the App Store or Google Play.';

function currentStore(): Store | null {
  return platform === 'android' || platform === 'ios' ? platform : null;
}

type PluginModule = typeof import('@capgo/native-purchases');
let pluginLoad: Promise<PluginModule | null> | null = null;

function plugin(): Promise<PluginModule | null> {
  if (!currentStore()) return Promise.resolve(null);
  pluginLoad ??= import('@capgo/native-purchases').catch(() => null);
  return pluginLoad;
}

/**
 * Stable opaque UUID for an account: StoreKit's appAccountToken / Play's obfuscated account id.
 * Lets the store list only this account's purchases on a shared device.
 */
export function accountToken(userId: string): string {
  let hex = '';
  for (let seed = 1; seed <= 4; seed++) {
    let h = Math.imul(0x811c9dc5 ^ seed, 0x9e3779b1);
    for (let i = 0; i < userId.length; i++) h = Math.imul(h ^ userId.charCodeAt(i), 0x01000193);
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
    h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
    hex += ((h ^ (h >>> 15)) >>> 0).toString(16).padStart(8, '0');
  }
  const variant = ((parseInt(hex[16], 16) & 3) | 8).toString(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

// Outbox ----------------------------------------------------------------------------------------------

const keyOf = (r: StoreReceipt): string => `${r.platform}:${r.platform === 'ios' ? (r.transactionId ?? r.receipt) : r.receipt}`;

function readOutbox(): StoreReceipt[] {
  try {
    const list = JSON.parse(localStorage.getItem(OUTBOX_KEY) ?? '[]') as unknown;
    return Array.isArray(list) ? (list as StoreReceipt[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(list: StoreReceipt[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(list.slice(-20)));
  } catch {
    /* storage full or blocked: the store's own purchase list still covers recovery */
  }
}

function remember(r: StoreReceipt): void {
  const k = keyOf(r);
  writeOutbox([...readOutbox().filter((x) => keyOf(x) !== k), r]);
}

function forget(r: StoreReceipt): void {
  const k = keyOf(r);
  writeOutbox(readOutbox().filter((x) => keyOf(x) !== k));
}

// Store plumbing ------------------------------------------------------------------------------------

function packForSku(sku: string, store: Store): GemPack | undefined {
  return GEM_PACKS.find((p) => p.skus[store] === sku);
}

function toReceipt(tx: Transaction, userId: string): StoreReceipt | null {
  const store = currentStore();
  const pack = store && packForSku(tx.productIdentifier, store);
  if (!store || !pack) return null;
  if (store === 'android') {
    if (!tx.purchaseToken) return null;
    return { platform: store, packId: pack.id, receipt: tx.purchaseToken, transactionId: tx.transactionId || undefined, userId, at: Date.now() };
  }
  if (!tx.transactionId) return null;
  return { platform: store, packId: pack.id, receipt: tx.jwsRepresentation || tx.transactionId, transactionId: tx.transactionId, userId, at: Date.now() };
}

/** Finishes (iOS) / acknowledges (Android) a credited purchase. Already-consumed purchases just fail quietly. */
async function finish(r: StoreReceipt): Promise<void> {
  const mod = await plugin();
  const token = r.platform === 'android' ? r.receipt : r.transactionId;
  if (!mod || !token) return;
  try {
    await mod.NativePurchases.acknowledgePurchase({ purchaseToken: token });
  } catch {
    /* already finished or consumed server-side */
  }
}

// Public API ----------------------------------------------------------------------------------------

/** Opens the store's purchase sheet for a gem pack. The receipt is saved before anything else happens. */
export async function startPurchase(sku: string, userId: string): Promise<StartResult> {
  const mod = await plugin();
  if (!mod) return { kind: 'failed', message: NOT_SOLD_HERE };
  let tx: Transaction;
  try {
    tx = await mod.NativePurchases.purchaseProduct({
      productIdentifier: sku,
      productType: mod.PURCHASE_TYPE.INAPP,
      quantity: 1,
      appAccountToken: accountToken(userId),
      // Finished/consumed only after the server has credited the gems.
      isConsumable: false,
      autoAcknowledgePurchases: false,
    });
  } catch (e) {
    const err = e as { code?: string; message?: string } | undefined;
    const msg = err?.message ?? '';
    if (err?.code === 'USER_CANCELLED' || /cancel/i.test(msg)) return { kind: 'cancelled' };
    if (/pending/i.test(msg)) return { kind: 'deferred' };
    return { kind: 'failed', message: msg || 'The purchase could not be completed.' };
  }
  const receipt = toReceipt(tx, userId);
  if (!receipt) return { kind: 'failed', message: 'The store did not confirm the purchase.' };
  remember(receipt);
  return { kind: 'purchased', receipt };
}

/** Receipts the server refused this session (not resubmitted until the next launch). */
const refused = new Set<string>();

/** Sends a receipt to the API; finishes the store transaction once the gems are credited. */
export async function settle(r: StoreReceipt): Promise<SettleResult> {
  let res: VerifyPurchaseResponse;
  try {
    res = await api.iap.verify({ platform: r.platform, packId: r.packId, receipt: r.receipt, transactionId: r.transactionId });
  } catch (e) {
    const code = errorCode(e);
    if (isNetworkError(e) || code === 'server' || code === 'unavailable' || code === 'rate_limited' || code === 'unauthorized' || code === 'unknown') {
      return { kind: 'retry', message: errorMessage(e) };
    }
    // Refused for good (cancelled, refunded, another account's). The transaction is deliberately left
    // unfinished: Google refunds unacknowledged purchases, and support can still look it up on iOS.
    refused.add(keyOf(r));
    forget(r);
    return { kind: 'rejected', message: errorMessage(e) };
  }
  if (res.status !== 'completed') return { kind: 'pending', res };
  await finish(r);
  forget(r);
  return { kind: 'credited', res };
}

let recovering = false;

/**
 * Delivers purchases that were paid for but never credited: the outbox plus the store's list of
 * unfinished purchases for this account. Safe to call often (launch, resume, opening the store).
 */
export async function recoverPurchases(userId: string, onCredited: (res: VerifyPurchaseResponse) => void): Promise<void> {
  const store = currentStore();
  if (!store || recovering) return;
  recovering = true;
  try {
    const queue = readOutbox().filter((r) => r.userId === userId && r.platform === store);
    const mod = await plugin();
    if (mod) {
      try {
        const { purchases } = await mod.NativePurchases.getPurchases({ productType: mod.PURCHASE_TYPE.INAPP, appAccountToken: accountToken(userId) });
        for (const tx of purchases) {
          if (store === 'android' && tx.purchaseState !== undefined && tx.purchaseState !== '1') continue; // still pending
          const r = toReceipt(tx, userId);
          if (r) queue.push(r);
        }
      } catch {
        /* store unavailable (no Play services, signed out of the store) */
      }
    }
    const seen = new Set<string>();
    for (const r of queue) {
      const k = keyOf(r);
      if (seen.has(k) || refused.has(k)) continue;
      seen.add(k);
      const out = await settle(r);
      if (out.kind === 'credited') onCredited(out.res);
      else if (out.kind === 'retry') break;
    }
  } finally {
    recovering = false;
  }
}

let watching = false;

/**
 * iOS delivers some purchases outside the purchase sheet (Ask to Buy approvals, interrupted
 * purchases). The plugin finishes those itself, so they go to the outbox before being credited.
 */
export async function watchPurchases(currentUser: () => string | null, onCredited: (res: VerifyPurchaseResponse) => void): Promise<void> {
  const mod = await plugin();
  if (!mod || currentStore() !== 'ios' || watching) return;
  watching = true;
  await mod.NativePurchases.addListener('transactionUpdated', (tx) => {
    const userId = currentUser();
    if (!userId) return;
    if (tx.appAccountToken && tx.appAccountToken.toLowerCase() !== accountToken(userId)) return;
    const r = toReceipt(tx, userId);
    if (!r) return;
    remember(r);
    void settle(r).then((out) => {
      if (out.kind === 'credited') onCredited(out.res);
    });
  });
}
